<template>
  <div class="on-autocomplete-wrapper">
    <el-autocomplete
      v-model="inputValue"
      :fetch-suggestions="internalFetchSuggestions"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxlength="maxLength"
      :name="name"
      :popper-class="popperClass"
      :trigger-on-focus="triggerOnFocus"
      :select-when-unmatched="selectWhenUnmatched"
      :label="label"
      :prefix-icon="prefixIcon"
      :suffix-icon="suffixIcon"
      :debounce="debounce"
      :placement="placement"
      :hide-loading="hideLoading"
      :popper-append-to-body="popperAppendToBody"
      :highlight-first-item="highlightFirstItem"
      :clearable="clearable"
      :value-key="valueKey"
      :autocomplete="autocomplete"
      class="on-autocomplete"
      :class="{ 'is-error': error }"
      v-bind="$attrs"
      @select="handleSelect"
      @input="handleInput"
      @change="handleChange"
      @focus="handleFocus"
      @blur="handleBlur"
    >
      <template v-if="$slots.prepend" slot="prepend">
        <slot name="prepend"></slot>
      </template>
      <template v-if="$slots.append" slot="append">
        <slot name="append"></slot>
      </template>
      <template v-if="$slots.prefix" slot="prefix">
        <slot name="prefix"></slot>
      </template>
      <template v-if="$slots.suffix" slot="suffix">
        <slot name="suffix"></slot>
      </template>
      <template v-if="$scopedSlots.default" slot-scope="{ item }">
        <slot :item="item"></slot>
      </template>
    </el-autocomplete>
  </div>
</template>

<script>
// 导入邮箱选项
import EMAIL_OPTIONS from '@/utils/tools/email';

export default {
  name: 'OnAutocomplete',
  inheritAttrs: false,

  props: {
    // 输入框值
    value: {
      type: [String, Number],
      default: '',
    },
    // 获取建议方法 (现在是可选的)
    fetchSuggestions: {
      type: Function,
      required: false,
      default: null,
    },
    // 提示文本
    placeholder: {
      type: String,
      default: '',
    },
    // 禁用状态
    disabled: {
      type: Boolean,
      default: false,
    },
    // 最大长度
    maxLength: {
      type: [Number, String],
      default: undefined,
    },
    // 错误状态
    error: {
      type: Boolean,
      default: false,
    },
    // 输入框名称
    name: {
      type: String,
      default: '',
    },
    // 自动完成
    autocomplete: {
      type: String,
      default: 'off',
    },
    // 自定义建议下拉框的类名
    popperClass: {
      type: String,
      default: '',
    },
    // 在输入框获得焦点时是否显示建议列表
    triggerOnFocus: {
      type: Boolean,
      default: true,
    },
    // 是否在点击匹配建议时 select
    selectWhenUnmatched: {
      type: Boolean,
      default: false,
    },
    // 输入框头部文字
    label: {
      type: String,
      default: '',
    },
    // 输入框头部图标
    prefixIcon: {
      type: String,
      default: '',
    },
    // 输入框尾部图标
    suffixIcon: {
      type: String,
      default: '',
    },
    // 获取输入建议的去抖延时
    debounce: {
      type: Number,
      default: 300,
    },
    // 菜单弹出位置
    placement: {
      type: String,
      default: 'bottom-start',
    },
    // 是否隐藏远程加载时的加载图标
    hideLoading: {
      type: Boolean,
      default: false,
    },
    // 是否将下拉列表插入至 body 元素
    popperAppendToBody: {
      type: Boolean,
      default: true,
    },
    // 是否默认高亮第一个选项
    highlightFirstItem: {
      type: Boolean,
      default: false,
    },
    // 是否可清空
    clearable: {
      type: Boolean,
      default: false,
    },
    // 输入建议对象中用于显示的键名
    valueKey: {
      type: String,
      default: 'value',
    },
    // 建议项
    suggestions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      inputValue: this.value,
    };
  },

  watch: {
    value(newVal) {
      this.inputValue = newVal;
    },
  },

  methods: {
    // 内部处理fetchSuggestions逻辑
    internalFetchSuggestions(queryString, cb) {
      // 如果外部提供了fetchSuggestions方法，则优先使用
      if (this.fetchSuggestions) {
        return this.fetchSuggestions(queryString, cb);
      }

      // 默认实现邮箱自动补全逻辑
      let results = [];
      try {
        const i = queryString.indexOf('@');
        if (i > -1 && i < queryString.length - 1) {
          results = EMAIL_OPTIONS.filter(item => item.value.includes(queryString.substring(i)))
            ?.map(item => ({ value: queryString.split('@')[0] + item.value }));
        }
      } catch (err) {
        // 错误处理
      }
      cb(results);
    },

    // 处理选择
    handleSelect(item) {
      this.$emit('select', item);
    },

    // 处理输入
    handleInput(value) {
      this.$emit('input', value);
    },

    // 处理变更
    handleChange(value) {
      this.$emit('change', value);
    },

    // 处理聚焦
    handleFocus(event) {
      this.$emit('focus', event);
    },

    // 处理失焦
    handleBlur(event) {
      this.$emit('blur', event);
    },
  },
};
</script>

<style lang="scss" scoped>
.on-autocomplete-wrapper {
  width: 100%;

  .on-autocomplete {
    width: 100%;

    &.is-error {
      :deep(.el-input__inner) {
        border-color: #F53F3F;
      }
    }
  }
}

:deep(.el-input__inner) {
  background: #FFFFFF;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  border: 1px solid #D0D5DD;
  height: 38px !important;
  &:focus {
    border-color: var(--brand-color) !important;
    box-shadow: 0px 0px 0px 3px #F1F1FF;
  }
}

:deep(.el-input.is-focus .el-input__inner) {
  border-color: var(--brand-color);
}
</style>
